import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  CardMedia, 
  Grid, 
  Box, 
  Divider, 
  CircularProgress,
  Chip,
  Button,
  Link
} from '@mui/material';
import { Calendar, User } from 'lucide-react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Link as RouterLink } from 'react-router-dom';

// Importez cette constante ou créez un fichier de configuration
const CLOUDINARY_CLOUD_NAME = 'dhke5v8gz';
// URL du placeholder
const PLACEHOLDER_URL = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/v1745764847/agrivision_placeholder_cklmxu.jpg`;

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const newsQuery = query(
          collection(db, 'news'),
          orderBy('createdAt', 'desc')
        );
        
        const newsSnapshot = await getDocs(newsQuery);
        
        if (newsSnapshot.empty) {
          setNews([]);
        } else {
          const newsData = newsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date()
          }));
          setNews(newsData);
        }
      } catch (err) {
        console.error('Erreur lors du chargement des actualités:', err);
        setError('Impossible de charger les actualités. ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchNews();
  }, []);

  // Fonction pour formater la date
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Fonction pour générer un slug à partir du titre
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ç]/g, 'c')
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <CircularProgress sx={{ color: '#014527' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" color="error" align="center">
          {error}
        </Typography>
      </Container>
    );
  }

  if (news.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" align="center" sx={{ color: '#014527', mb: 2 }}>
          Actualités
        </Typography>
        <Divider sx={{ mb: 4 }} />
        <Box sx={{ 
          backgroundColor: '#f5f5f5', 
          p: 4, 
          borderRadius: 2,
          textAlign: 'center'
        }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Aucune actualité disponible pour le moment
          </Typography>
          <Typography variant="body1">
            Revenez bientôt pour découvrir les dernières nouvelles d'Agri Vision Gabon.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" align="center" sx={{ color: '#014527', mb: 2 }}>
        Actualités
      </Typography>
      <Divider sx={{ mb: 4 }} />

      {/* Article principal (le plus récent) */}
      <Card sx={{ 
        mb: 6, 
        borderRadius: 2, 
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          transform: 'translateY(-4px)',
          transition: 'all 0.3s ease'
        }
      }}>
        <Grid container>
          <Grid item xs={12} md={6}>
            <CardMedia
              component="img"
              height="400"
              image={news[0].imageUrl || PLACEHOLDER_URL}
              alt={news[0].title}
              sx={{ 
                height: { xs: '250px', md: '400px' },
                objectFit: 'cover'
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ mb: 2 }}>
                <Chip 
                  label="À la une" 
                  sx={{ 
                    bgcolor: '#014527', 
                    color: 'white', 
                    fontWeight: 'bold',
                    mb: 2
                  }} 
                />
              </Box>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                {news[0].title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: 'text.secondary' }}>
                <Calendar size={16} />
                <Typography variant="body2" sx={{ ml: 1, mr: 3 }}>
                  {formatDate(news[0].createdAt)}
                </Typography>
                <User size={16} />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {news[0].author || 'Agri Vision Gabon'}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 2, flexGrow: 1 }}>
                {news[0].summary || news[0].content.substring(0, 200) + '...'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                    variant="contained" 
                    component={RouterLink}
                    to={`/news/${news[0].id}`}
                    sx={{ 
                    bgcolor: '#014527', 
                    '&:hover': { bgcolor: '#01331d' } 
                    }}
                >
                    Lire la suite
                </Button>
                
                {news[0].externalLink && (
                    <Button 
                    variant="outlined" 
                    href={news[0].externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                        color: '#014527', 
                        borderColor: '#014527',
                        '&:hover': { 
                        borderColor: '#01331d',
                        backgroundColor: 'rgba(1, 69, 39, 0.05)' 
                        } 
                    }}
                    >
                    {news[0].linkLabel || 'En savoir plus'}
                    </Button>
                )}
                </Box>
            </CardContent>
          </Grid>
        </Grid>
      </Card>

      {/* Articles secondaires */}
      <Typography variant="h5" sx={{ mb: 3, color: '#014527' }}>
        Autres actualités
      </Typography>
      
      <Grid container spacing={3}>
        {news.slice(1).map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.id}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}>
              <CardMedia
                component="img"
                height="200"
                image={item.imageUrl || PLACEHOLDER_URL}
                alt={item.title}
              />
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {item.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Calendar size={14} />
                  <Typography variant="body2" sx={{ ml: 1, fontSize: '0.8rem' }}>
                    {formatDate(item.createdAt)}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {item.summary || item.content.substring(0, 120) + '...'}
                </Typography>
                <Button 
                    variant="outlined" 
                    size="small" 
                    component={RouterLink}
                    to={`/news/${item.id}`}
                    sx={{ 
                    color: '#014527', 
                    borderColor: '#014527',
                    '&:hover': { 
                        borderColor: '#01331d',
                        backgroundColor: 'rgba(1, 69, 39, 0.05)' 
                    } 
                    }}
                >
                    Lire la suite
                </Button>
                
                {item.externalLink && (
                    <Button 
                    variant="text" 
                    size="small"
                    href={item.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                        color: '#014527',
                        '&:hover': { 
                        backgroundColor: 'rgba(1, 69, 39, 0.05)' 
                        } 
                    }}
                    >
                    {item.linkLabel || 'Lien'}
                    </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>


    </Container>
  );
};

export default NewsPage;